import React, { useEffect, useRef, useState } from 'react';
import { Link, router, usePage } from '@inertiajs/react';
import type { SharedProps } from '../../types';

// 边栏顶部的品牌块：`a` 方块 + asaptodo + `⌄`。设计稿画的那一行。
//
// 那个 `⌄` 不是装饰，它是**全站导航的落点**。wiki 下没有全站 `Header` 了
// （照设计稿：100vh 两栏、没有顶部横条），而 `Header` 原来扛着六个顶级项和
// 一整个账户下拉 —— 番茄钟 / 规划(3) / 统计(4) / 设置 / 接入 / 后台 / 退出。
// 那些页面在整个前端**只有 Header 一个入口**（`Header.tsx` 的注释写着「删掉
// 就等于让页面失联」）。所以它们全部搬进这个菜单，一个都不少。
//
// 边栏那几行（待办 / wiki / 日志）是设计稿画出来的三个高频入口，与这里不重复：
// 菜单装的是**其余全部**。
//
// 不引 antd：这一带刻意把它挡在外面（`TaskContextMenu` 的注释讲过理由），
// 而且 `Header` 里那个 `NavDropdown` 已经手写过一遍同样的东西 —— 但不复用它：
// 它长在 `Header` 的类名体系里（gray-*、border-b-2 的下划线态），搬到暖米色板
// 的边栏上要逐条改，而改完就已经是另一个组件了。

type Item = { href: string; label: string };
type Section = { label: string | null; items: Item[] };

// 与 `Header.tsx` 的 NAV_AUTH + 账户下拉一一对应。**少一项就是一个页面失联**，
// 所以这张表要跟着那边一起改 —— `test/frontend_nav_coverage_test.rb` 钉住这一条。
const SECTIONS: Section[] = [
  { label: null, items: [{ href: '/', label: '番茄钟' }] },
  {
    label: '规划',
    items: [
      { href: '/tracker/plan', label: '计划' },
      { href: '/tracker/projects', label: '项目' },
      { href: '/ai-tasker', label: 'AI Tasker' },
    ],
  },
  {
    label: '统计',
    items: [
      { href: '/dashboard', label: '仪表盘' },
      { href: '/insights', label: '数据洞察' },
      { href: '/calendar', label: '日历' },
      { href: '/history', label: '历史记录' },
    ],
  },
];

// 账户那一组。`后台` 只有 admin 看得见，所以不在上面那张静态表里。
const ACCOUNT: Item[] = [
  { href: '/settings', label: '设置' },
  { href: '/developers', label: '接入' },
];

const BrandMenu: React.FC = () => {
  const { props } = usePage<SharedProps>();
  const user = props.auth?.user;
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // 点外面关、Esc 关。照 Header 里那个 NavDropdown 的做法 —— pointerdown 而不是
  // click：click 会在按下的元素被 React 卸载之后才触发，那时 contains 判断已经
  // 不成立了。
  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const link = (item: Item) => (
    <Link
      key={item.href}
      href={item.href}
      // 面板里只有链接，所以在面板上兜一个 onClick 就够，不用逐个挂
      // （照 Header 的 NavDropdown）。
      className="block rounded-md px-2 py-1.5 text-[13px] text-todo-ink2
                 hover:bg-wiki-sideHover hover:text-todo-ink"
    >
      {item.label}
    </Link>
  );

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left
                   hover:bg-wiki-sideHover"
      >
        <span
          className="grid h-5 w-5 shrink-0 place-items-center rounded-[5px] bg-todo-ink
                     text-[11px] font-bold text-todo-side"
        >
          a
        </span>
        <span className="truncate text-[13.5px] font-semibold text-todo-ink">asaptodo</span>
        <span className="ml-auto shrink-0 text-[12px] text-todo-muted">⌄</span>
      </button>

      {open && (
        <div
          onClick={() => setOpen(false)}
          className="absolute left-1 right-1 top-full z-40 mt-1 rounded-lg border border-todo-line
                     bg-todo-card p-1 shadow-lg"
        >
          {SECTIONS.map((section) => (
            <div key={section.label ?? 'top'}>
              {section.label && (
                <div className="px-2 pb-0.5 pt-2 text-[11px] font-semibold tracking-wide text-todo-muted">
                  {section.label}
                </div>
              )}
              {section.items.map(link)}
            </div>
          ))}

          <div className="my-1 border-t border-todo-line" />
          {ACCOUNT.map(link)}
          {user?.admin && link({ href: '/admin', label: '后台' })}
          <button
            type="button"
            onClick={() => router.delete('/logout')}
            className="block w-full rounded-md px-2 py-1.5 text-left text-[13px] text-todo-ink2
                       hover:bg-wiki-sideHover hover:text-todo-ink"
          >
            退出
          </button>
        </div>
      )}
    </div>
  );
};

export default BrandMenu;
